/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { useCallback, useRef, useState } from "react";

import {
  colors,
  constantSemanticColors,
  numericPercentages,
  numericValues,
  spacing,
} from "../../../../../../global/emotion";
import { IconButton } from "../../../../../components/_base/_core/IconButton/IconButton.emotion";
import { VolumeIcon } from "../../../../../components/_base/icons/Icon/MediaIcons";
import { useThemeValues } from "../../../../../hooks/useThemeValues";
import type { Color } from "../../../../../types/color.emotion.types";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";
import type { WithButtonWrapper } from "../../../../../types/withButtonWrapper.types";
import { FragmentAsChild } from "../../../primitives/FragmentAsChild/FragmentAsChild";
import { Slider } from "../../../primitives/Slider/Slider.emotion";

const containerStyle: Interpolation = {
  columnGap: spacing.ms,
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  placeContent: "flex-start",
  placeItems: "center",
  pointerEvents: "all",
};
const containerFullStyle: Interpolation = {
  placeContent: "center",
  width: numericPercentages[100],
};
const iconStyle: Interpolation = {
  padding: numericValues[0],
};
const sliderStyle: Interpolation = {
  padding: spacing.none,
  width: spacing.xxxl,
};
const sliderFullStyle: Interpolation = {
  width: numericPercentages[100],
};

export type VolumeControlProps = WithButtonWrapper<
  Omit<HallowElementProps<"div">, "onVolumeChange"> & {
    onVolumeChange: (newVol: number) => void;
    value: number;
    iconColor?: Color;
    onOverlay?: boolean;
    fullWidth?: boolean;
  }
>;

export const VolumeControl = ({
  onVolumeChange,
  value,
  iconColor = "white",
  onOverlay = false,
  fullWidth = false,
  Wrapper = FragmentAsChild,
  ...props
}: VolumeControlProps) => {
  const t = useTranslations();
  const [volume, setVolume] = useState<number>((value ?? 1) * 100);
  const lastVolume = useRef<number>(volume);
  const theme = useThemeValues();

  const updateVolume = useCallback(
    ([newVolume]: [number]) => {
      lastVolume.current = volume;
      onVolumeChange(newVolume / 100.0);
      setVolume(newVolume);
    },
    [volume],
  );

  const mute = useCallback(() => {
    if (volume === 0) updateVolume([lastVolume.current]);
    else updateVolume([0]);
  }, [volume]);

  const Icon =
    volume === 0
      ? VolumeIcon.Mute
      : volume < 50
        ? VolumeIcon.Min
        : VolumeIcon.Max;

  return (
    <div
      css={[containerStyle, fullWidth ? containerFullStyle : null]}
      onClick={(e) => e.stopPropagation()}
      {...props}
    >
      <Wrapper asChild>
        <IconButton
          icon={
            <Icon
              fill={
                theme?.semanticColors?.[iconColor] ??
                colors[iconColor] ??
                constantSemanticColors[iconColor] ??
                iconColor
              }
              css={iconStyle}
            />
          }
          variant="transparentWhite"
          onClick={mute}
          title={t(
            volume === 0
              ? "web_mute_button_tooltip_unmute"
              : "web_mute_button_tooltip",
          )}
          tabIndex={0}
        />
      </Wrapper>
      <Slider
        size={"small"}
        value={volume}
        defaultValue={100}
        onValueChange={updateVolume}
        css={[sliderStyle, fullWidth ? sliderFullStyle : null]}
        onColor={onOverlay}
      />
    </div>
  );
};
